import { z } from "zod";
import { CountryCode } from "./country.js";
/**
 * Address lines shared by every country. What `city` and `region` mean depends on the country: city, town,
 * suburb, or district; state, province, prefecture, or county. ADDRESS_FORMATS says which are required.
 */
export const AddressFields = z
    .object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(100).optional(),
    region: z.string().trim().max(100).optional(),
    postalCode: z.string().trim().max(20).optional(),
})
    .meta({ id: "AddressFields" });
/** Ukraine's oblasts, cities with special status, and Crimea, by ISO 3166-2 code. */
export const UA_REGIONS = [
    "UA-05",
    "UA-07",
    "UA-09",
    "UA-12",
    "UA-14",
    "UA-18",
    "UA-21",
    "UA-23",
    "UA-26",
    "UA-30",
    "UA-32",
    "UA-35",
    "UA-40",
    "UA-43",
    "UA-46",
    "UA-48",
    "UA-51",
    "UA-53",
    "UA-56",
    "UA-59",
    "UA-61",
    "UA-63",
    "UA-65",
    "UA-68",
    "UA-71",
    "UA-74",
    "UA-77",
];
/** Rules for the launch corridors (decision 0006) and nearby markets; other countries need line1 and city. */
export const ADDRESS_FORMATS = {
    SG: { required: ["postalCode"], postalCode: /^\d{6}$/ },
    MY: { required: ["city", "region", "postalCode"], postalCode: /^\d{5}$/ },
    HK: { required: ["city", "region"], regions: ["HK", "KLN", "NT"], noPostalCode: true },
    CN: { required: ["region", "city", "postalCode"], postalCode: /^\d{6}$/ },
    JP: { required: ["region", "city", "postalCode"], postalCode: /^\d{3}-?\d{4}$/ },
    AU: {
        required: ["city", "region", "postalCode"],
        postalCode: /^\d{4}$/,
        regions: ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"],
    },
    US: { required: ["city", "region", "postalCode"], postalCode: /^\d{5}(-\d{4})?$/ },
    CA: { required: ["city", "region", "postalCode"], postalCode: /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i },
    GB: { required: ["city", "postalCode"], postalCode: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i },
    IE: { required: ["city"], postalCode: /^[A-Z]\d{2} ?[A-Z\d]{4}$/i },
    DE: { required: ["city", "postalCode"], postalCode: /^\d{5}$/ },
    FR: { required: ["city", "postalCode"], postalCode: /^\d{5}$/ },
    NL: { required: ["city", "postalCode"], postalCode: /^\d{4} ?[A-Z]{2}$/i },
    // Ukraine's regions by ISO 3166-2 code, so the regions Civentia does not serve are known (ADR 0025, card 127).
    UA: { required: ["region", "city", "postalCode"], postalCode: /^\d{5}$/, regions: UA_REGIONS },
};
const DEFAULT_FORMAT = { required: ["city"] };
export function addressFormat(country) {
    return ADDRESS_FORMATS[country] ?? DEFAULT_FORMAT;
}
/** Field errors for an address in a country, as paths relative to `fields`; empty when valid. */
export function addressProblems(country, fields) {
    const format = addressFormat(country);
    const problems = [];
    for (const field of format.required) {
        const value = fields[field];
        if (value === undefined || value.length === 0)
            problems.push({ path: field, message: `Required in ${country}` });
    }
    const postal = fields.postalCode;
    if (postal !== undefined && postal.length > 0) {
        if (format.noPostalCode === true)
            problems.push({ path: "postalCode", message: `${country} has no postal codes` });
        else if (format.postalCode !== undefined && !format.postalCode.test(postal)) {
            problems.push({ path: "postalCode", message: `Not a ${country} postal code` });
        }
    }
    const region = fields.region;
    if (format.regions !== undefined &&
        region !== undefined &&
        region.length > 0 &&
        !format.regions.includes(region)) {
        problems.push({ path: "region", message: `One of ${format.regions.join(", ")}` });
    }
    return problems;
}
const Phone = z.string().regex(/^\+[1-9]\d{6,14}$/, "E.164 phone number");
const addressShape = {
    label: z.string().trim().min(1).max(60),
    country: CountryCode,
    fields: AddressFields,
    contactName: z.string().trim().min(1).max(100).nullable().optional(),
    contactPhone: Phone.nullable().optional(),
};
function checkCountry(value, context) {
    for (const problem of addressProblems(value.country, value.fields)) {
        context.addIssue({ code: "custom", path: ["fields", problem.path], message: problem.message });
    }
}
/** A new address; `isDefaultDelivery` and `isDefaultOrigin` move the enterprise's default to it. */
export const AddressInput = z
    .object({
    ...addressShape,
    isDefaultDelivery: z.boolean().default(false),
    isDefaultOrigin: z.boolean().default(false),
})
    .superRefine(checkCountry)
    .meta({ id: "AddressInput" });
/** An edit replaces the address's country and fields together, so they are always validated as a pair. */
export const AddressPatch = z
    .object({
    label: addressShape.label.optional(),
    location: z.object({ country: CountryCode, fields: AddressFields }).superRefine(checkCountry).optional(),
    contactName: addressShape.contactName,
    contactPhone: addressShape.contactPhone,
    isDefaultDelivery: z.literal(true).optional().describe("Moves the default delivery address here"),
    isDefaultOrigin: z.literal(true).optional().describe("Moves the default shipping origin here"),
})
    .meta({ id: "AddressPatch" });
//# sourceMappingURL=address.js.map